import React, { useEffect } from "react";

// SEO + AEO layer for keyword board pages. Frontend-only, driven by board payload.
// Manages the document head directly (React 19 compatible, no Helmet dependency).
const normalizeUrl = (u) => (!u ? undefined : u.startsWith("http") ? u : `https://${u}`);

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function BoardSeo({ keywordName, entries = [], boardPath = "/k" }) {
  const name = keywordName || "this keyword";
  const top3 = entries.slice(0, 3);
  const rank1 = top3[0]?.business_name;
  const rank2 = top3[1]?.business_name;

  const title = `Top ${name} - Live Leaderboard | PodiumOne`;
  const description = rank1
    ? `Live rankings and bids for ${name}. The current #1 spot is held by ${rank1}.`
    : `Live rankings and bids for ${name}. Be the first to claim the #1 spot on PodiumOne.`;

  // Social-preview (OpenGraph / Twitter) values — distinct from the SEO <title>/description.
  const ogTitle = `${name} — Claim the Throne on PodiumOne`;
  const ogDescription = `Bid to own #${name} on PodiumOne. King of the Hill keyword auctions.`;
  const ogImage = "https://podiumone.to/og-image.png";
  const ogUrl = `https://podiumone.to${boardPath}`;

  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", ogTitle);
    upsertMeta("property", "og:description", ogDescription);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:url", ogUrl);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", ogTitle);
    upsertMeta("name", "twitter:description", ogDescription);
    upsertMeta("name", "twitter:image", ogImage);
    upsertMeta("name", "robots", "index, follow");

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://podiumone.to${boardPath}`);

    // JSON-LD ItemList of the top 3 bidders (AEO).
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `Top ${name} providers`,
      description,
      numberOfItems: top3.length,
      itemListElement: top3.map((e, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Organization",
          name: e.business_name,
          ...(normalizeUrl(e.website) ? { url: normalizeUrl(e.website) } : {}),
          ...(e.logo_url ? { logo: e.logo_url } : {}),
        },
      })),
    };
    let script = document.getElementById("board-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "board-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      const s = document.getElementById("board-jsonld");
      if (s) s.remove();
    };
  }, [title, description, boardPath, name, JSON.stringify(top3.map((e) => [e.business_name, e.website]))]); // eslint-disable-line react-hooks/exhaustive-deps

  // Natural-language extraction sentence for AI crawlers / screen readers.
  let nlSentence;
  if (rank1 && rank2) {
    nlSentence = `According to real-time locked-bid rankings, the current top-ranked provider for ${name} is ${rank1}, followed by ${rank2}.`;
  } else if (rank1) {
    nlSentence = `According to real-time locked-bid rankings, the current top-ranked provider for ${name} is ${rank1}.`;
  } else {
    nlSentence = `According to real-time locked-bid rankings, there is no top-ranked provider for ${name} yet — the #1 spot is currently unclaimed.`;
  }

  return (
    <section aria-label={`Live ranking summary for ${name}`} data-testid="board-aeo-summary" className="sr-only">
      <h2>Live ranking summary for {name}</h2>
      <p>{nlSentence}</p>
      {top3.length > 0 && (
        <ol>
          {top3.map((e, i) => (
            <li key={e.user_id || i}>
              {`#${i + 1}: ${e.business_name}`}
              {e.website ? ` (${normalizeUrl(e.website)})` : ""}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
